import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Logo } from "../../components/site/Logo";
import { useAuth } from "../../lib/auth";
import { http, apiError } from "../../lib/api";

const Shell = ({ title, children, testid }) => (
  <div className="min-h-screen grid place-items-center px-5 py-16 grain relative" data-testid={testid}>
    <div className="relative w-full max-w-md border hairline bg-[var(--ink-2)] p-8 md:p-10">
      <Link to="/" className="inline-block mb-10"><Logo /></Link>
      <p className="eyebrow">Staff Dashboard</p>
      <h1 className="font-display text-4xl mt-3 mb-8">{title}</h1>
      {children}
    </div>
  </div>
);

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="w-4 h-4" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const startGoogle = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/admin/login";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const useGoogleCallback = () => {
  const { hash } = useLocation();
  const { googleLogin } = useAuth();
  const nav = useNavigate();
  const done = useRef(false);
  const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
  useEffect(() => {
    if (!sessionId || done.current) return;
    done.current = true;
    window.history.replaceState(null, "", window.location.pathname);
    googleLogin(sessionId)
      .then(() => nav("/admin", { replace: true }))
      .catch((err) => {
        toast.error(apiError(err));
        nav("/admin/login", { replace: true });
      });
  }, [sessionId, googleLogin, nav]);
  return !!sessionId;
};

export function AdminLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const processing = useGoogleCallback();
  if (processing) return <Shell title="Signing you in" testid="google-callback-page"><Loader2 className="w-6 h-6 animate-spin text-gold" /></Shell>;
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(f.email, f.password);
      nav("/admin");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Sign in" testid="admin-login-page">
      <form onSubmit={submit} className="space-y-5">
        <div><label className="label" htmlFor="lg-email">Email</label><input id="lg-email" type="email" required className="field" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email-input" /></div>
        <div><label className="label" htmlFor="lg-pw">Password</label><input id="lg-pw" type="password" required className="field" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password-input" /></div>
        <button className="btn-gold w-full" disabled={busy} data-testid="login-submit-button">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Sign in</button>
        <Link to="/admin/forgot-password" className="block text-center text-sm text-slate-300 hover:text-gold" data-testid="forgot-password-link">Forgot password?</Link>
      </form>
      <div className="flex items-center gap-3 my-6 text-xs text-slate-400"><span className="h-px flex-1 bg-white/10" />or<span className="h-px flex-1 bg-white/10" /></div>
      <button type="button" onClick={startGoogle} className="btn-ghost w-full justify-center" data-testid="google-login-button"><GoogleIcon /> Sign in with Google</button>
      <p className="text-xs text-slate-400 text-center mt-3">Use the Google account with the same email as your staff account.</p>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Reset password" testid="forgot-password-page">
      {sent ? (
        <p className="text-slate-300" data-testid="forgot-password-sent">If that email belongs to a dashboard account, a reset link has been sent. The link expires in 1 hour.</p>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <div><label className="label" htmlFor="fp-email">Account email</label><input id="fp-email" type="email" required className="field" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" /></div>
          <button className="btn-gold w-full" disabled={busy} data-testid="forgot-submit-button">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Send reset link</button>
        </form>
      )}
      <Link to="/admin/login" className="block text-center text-sm text-slate-300 hover:text-gold mt-6">Back to sign in</Link>
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await http.post("/auth/reset-password", { token: params.get("token") || "", password: pw.a });
      toast.success("Password updated. Please sign in.");
      nav("/admin/login");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Choose a new password" testid="reset-password-page">
      <form onSubmit={submit} className="space-y-5">
        <div><label className="label" htmlFor="rp-a">New password (min 8 characters)</label><input id="rp-a" type="password" minLength={8} required className="field" value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} data-testid="reset-password-input" /></div>
        <div><label className="label" htmlFor="rp-b">Confirm password</label><input id="rp-b" type="password" minLength={8} required className="field" value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} data-testid="reset-password-confirm-input" /></div>
        <button className="btn-gold w-full" disabled={busy} data-testid="reset-submit-button">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Update password</button>
      </form>
    </Shell>
  );
}
